"use client";

import { X } from "lucide-react";
import Image from "next/image";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type PreviewPayload = { src: string; alt: string; title: string } | null;

type PreviewContextValue = {
  setPreview: (next: PreviewPayload) => void;
  accent: "cyan" | "violet";
};

const PreviewContext = createContext<PreviewContextValue | null>(null);

export function ExamplePreviewProvider({
  accent,
  children,
}: {
  accent: "cyan" | "violet";
  children: ReactNode;
}) {
  const [preview, setPreview] = useState<PreviewPayload>(null);

  useEffect(() => {
    if (!preview) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [preview]);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  const ring =
    accent === "cyan"
      ? "ring-cyan-400/45 shadow-cyan-500/20"
      : "ring-violet-400/45 shadow-violet-500/20";

  return (
    <PreviewContext.Provider value={{ setPreview, accent }}>
      {children}
      {preview ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="example-preview-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/88 backdrop-blur-md"
            aria-label="Cerrar vista previa"
            onClick={() => setPreview(null)}
          />
          <div
            className={`relative z-10 flex max-h-[min(92vh,940px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/15 bg-zinc-950 shadow-2xl ring-2 ${ring}`}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2 sm:px-4">
              <p id="example-preview-title" className="truncate pl-1 text-sm font-semibold text-zinc-200">
                {preview.title}
              </p>
              <button
                type="button"
                className="shrink-0 rounded-xl p-2 text-zinc-400 transition hover:bg-white/10 hover:text-white"
                aria-label="Cerrar"
                onClick={() => setPreview(null)}
              >
                <X className="h-6 w-6" aria-hidden />
              </button>
            </div>
            <div className="relative min-h-0 flex-1 bg-black/40 p-2 sm:p-4">
              <div className="relative mx-auto h-[min(78vh,820px)] w-full max-w-2xl">
                <Image
                  src={preview.src}
                  alt={preview.alt}
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 100vw, 672px"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PreviewContext.Provider>
  );
}

export function ExamplePreviewTrigger({
  src,
  alt,
  previewTitle = "Resultado Selfia",
  className = "",
  children,
}: {
  src: string;
  alt: string;
  /** Título del modal de vista previa. */
  previewTitle?: string;
  className?: string;
  children: ReactNode;
}) {
  const ctx = useContext(PreviewContext);
  if (!ctx) {
    throw new Error("ExamplePreviewTrigger debe estar dentro de ExamplePreviewProvider");
  }

  const focusRing =
    ctx.accent === "cyan" ? "focus-visible:outline-cyan-400" : "focus-visible:outline-violet-400";

  return (
    <button
      type="button"
      className={`group relative block w-full cursor-zoom-in overflow-hidden border-0 bg-transparent p-0 text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-2 ${focusRing} ${className}`.trim()}
      onClick={() => ctx.setPreview({ src, alt, title: previewTitle })}
      aria-label={`Ampliar: ${alt}`}
    >
      {children}
    </button>
  );
}
